import { useCallback, useEffect, useMemo, useState } from 'react';

import { IllustCompleted, IllustWarning } from '@common/icons';
import { SiteId } from '@common/site-manager';
import { BrandColorMap } from '@common/styles';

import { OverlayCompProps } from '../common/util/createOverlayComp';
import { BottomDialog, BottomDialogButtonOption } from './dump/BottomDialog';
import { AlertReturnValue, DialogMessage, DialogMessageObject } from './Dialog';

export function renderAlert(message: DialogMessage) {
  return ({ resolve, dispose }: OverlayCompProps<AlertReturnValue>): JSX.Element => {
    if (typeof message === 'object') {
      return <RecordAlert {...{ ...message, resolve, dispose }} />;
    }
    return <RecordAlert {...{ message, resolve, dispose }} />;
  };
}

function RecordAlert({
  message,
  ok,
  title,
  type,
  resolve,
  dispose,
  variant,
  brandColor = BrandColorMap[SiteId.ZIGZAG],
}: DialogMessageObject & OverlayCompProps<AlertReturnValue>) {
  const [active, setActive] = useState(true);
  const proceed = useCallback(
    (value: AlertReturnValue) => {
      resolve?.(value);
      setActive(false);
      dispose?.();
    },
    [resolve, setActive, dispose],
  );
  const image = useMemo(() => {
    if (type === 'warning') {
      return <IllustWarning />;
    }
    if (type === 'complete') {
      return <IllustCompleted color={brandColor.primary} />;
    }
    return undefined;
  }, [type, brandColor]);
  const handleOutsideClick = useCallback(() => {
    proceed('skip');
  }, [proceed]);

  const buttons = useMemo<BottomDialogButtonOption[]>(
    () => [
      {
        id: 'ok',
        children: ok || '확인',
        onClick: () => {
          proceed('ok');
        },
        size: 'lg',
        variant: variant ?? 'primary',
      },
    ],
    [ok, proceed, variant],
  );

  useEffect(() => {
    return () => {
      dispose?.();
    };
  }, [dispose]);

  return (
    <BottomDialog
      active={active}
      image={image}
      title={title}
      text={message}
      handleOutsideClick={handleOutsideClick}
      buttons={buttons}
    />
  );
}

